import {
  getFirstProposalLine,
  getProposalLlmTokenCount,
  getProposalScopeLabel,
} from '../../../../../../helpers/proposals';
import { TRANSCRIPT_REVIEW_PROPOSAL_STATUS } from '../../../../../../constants';
import { compactSecondaryButtonClassName } from '../../../../../../styles';
import { TProps } from './types/TProps';

export const ProposalCard = ({ proposal, disabled, isPreviewed, onJumpToLine, onPreview }: TProps) => {
  const changes = proposal.patch.changes ?? [];
  const hasApplyProblem =
    proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.STALE || proposal.applyErrors.length > 0;

  return (
    <div
      className={`border rounded-[8px] p-3 flex flex-col gap-2 shadow-sm ${
        hasApplyProblem
          ? 'border-[var(--color-error-border)] bg-[var(--color-error-surface)]'
          : isPreviewed
            ? 'border-[var(--color-fixed-accent)] bg-[var(--color-tint)]'
            : 'border-[var(--color-border)] bg-[var(--color-surface)]'
      }`}
    >
      <div className="flex justify-between gap-3">
        <div>
          <div className="font-semibold text-[var(--color-title)]">
            #{proposal.sequence} {proposal.agentName || proposal.source}
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">
            revision {proposal.targetRevision} · {proposal.changedLines.length} lines
          </div>
          {getProposalScopeLabel(proposal) || getProposalLlmTokenCount(proposal) ? (
            <div className="text-xs text-[var(--color-text-muted)]">
              {[
                getProposalScopeLabel(proposal),
                getProposalLlmTokenCount(proposal) ? `${getProposalLlmTokenCount(proposal)} tokens` : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          ) : null}
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap justify-end">
          <button
            className={compactSecondaryButtonClassName}
            disabled={disabled}
            onClick={() => onPreview(proposal.id)}
          >
            {isPreviewed ? 'Hide' : 'Show'}
          </button>
          <button
            className={compactSecondaryButtonClassName}
            disabled={disabled || getFirstProposalLine(proposal) === null}
            onClick={() => onJumpToLine(proposal)}
          >
            Jump
          </button>
        </div>
      </div>

      {proposal.prompt ? <div className="text-sm text-[var(--color-title)]">{proposal.prompt}</div> : null}

      <div className="flex flex-col gap-2 max-h-[220px] overflow-auto">
        {changes.map((change) => (
          <div
            className="grid grid-cols-[56px_1fr] gap-2 text-xs border-t border-[var(--color-border)] pt-2"
            key={`${proposal.id}-${change.line}`}
          >
            <div className="text-[var(--color-text-muted)]">L{change.line}</div>
            <div className="flex flex-col gap-1">
              <div className="border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-2 rounded-[6px] whitespace-pre-wrap text-[var(--color-text)]">
                {change.expectedText}
              </div>
              <div className="border border-[var(--color-border)] bg-[var(--color-tint)] p-2 rounded-[6px] whitespace-pre-wrap text-[var(--color-text)]">
                {change.text}
              </div>
            </div>
          </div>
        ))}
      </div>

      {proposal.applyErrors.length ? (
        <div className="text-xs text-[var(--color-error-heading)]">{proposal.applyErrors.join('; ')}</div>
      ) : null}
    </div>
  );
};
